/**
 * Feature: READING PLANS — PURE logic (no UI, no DB).
 *
 * A plan is a STATIC template (see `data/plan-templates.ts`): a
 * sequence of days, each with 1..n chapters. The only persisted state is
 * user PROGRESS (`bible_reading_progress`: one row per completed
 * completed, soft-delete with `deleted_at`). These functions derive the view
 * (completed days, next day, percentage) from these rows. Pure and
 * deterministic: same inputs -> same output.
 */

/** Specific chapter to read (canonical book/chapter coordinate). */
export interface PlanReading {
  book: number;
  chapter: number;
}

/** Plan day: one or more contiguous chapters. */
export interface PlanDay {
  readings: PlanReading[];
}

/**
 * Minimal progress row (`bible_reading_progress` projection). `day` is
 * 1-based index within plan; soft-deleted rows MUST NOT reach here (the
 * caller filtra `deleted_at IS NULL`).
 */
export interface PlanProgressRow {
  plan_id: string | null;
  day: number | null;
}

/** Derived view of plan progress. */
interface PlanProgressView {
  /** Completed days (multi-device dedupe), bounded to valid plan days. */
  completedDays: ReadonlySet<number>;
  completedCount: number;
  totalDays: number;
  /** 0..100, redondeado. */
  percent: number;
  /** First uncompleted day (1-based); `null` if plan finished. */
  nextDay: number | null;
  isComplete: boolean;
  /** Plan has at least one completed day. */
  isStarted: boolean;
}

/**
 * Completed days for ONE plan from progress rows: filters by
 * `plan_id`, drops out-of-range days (templates may evolve) and
 * dedupes (two devices may have marked same day offline).
 */
export function completedDaysOf(
  rows: readonly PlanProgressRow[],
  planId: string,
  totalDays: number,
): Set<number> {
  const days = new Set<number>();
  for (const row of rows) {
    if (row.plan_id !== planId || row.day === null) {
      continue;
    }
    if (Number.isInteger(row.day) && row.day >= 1 && row.day <= totalDays) {
      days.add(row.day);
    }
  }
  return days;
}

const PERCENT_MAX = 100;

/** Derives progress view of a plan (pure). */
export function computePlanProgress(
  rows: readonly PlanProgressRow[],
  planId: string,
  totalDays: number,
): PlanProgressView {
  const completedDays = completedDaysOf(rows, planId, totalDays);
  const completedCount = completedDays.size;
  let nextDay: number | null = null;
  for (let day = 1; day <= totalDays; day += 1) {
    if (!completedDays.has(day)) {
      nextDay = day;
      break;
    }
  }
  const isComplete = totalDays > 0 && completedCount >= totalDays;
  return {
    completedDays,
    completedCount,
    totalDays,
    percent: totalDays === 0 ? 0 : Math.round((completedCount / totalDays) * PERCENT_MAX),
    nextDay,
    isComplete,
    isStarted: completedCount > 0,
  };
}

/**
 * Distributes chapters (canonical order) across `dayCount` CONTIGUOUS days of
 * balanced size (differing by at most 1, heavier days first).
 * Sequential template generator.
 */
export function splitIntoDays(readings: readonly PlanReading[], dayCount: number): PlanDay[] {
  if (dayCount <= 0) {
    return [];
  }
  const days: PlanDay[] = [];
  const base = Math.floor(readings.length / dayCount);
  const remainder = readings.length % dayCount;
  let cursor = 0;
  for (let i = 0; i < dayCount; i += 1) {
    const size = base + (i < remainder ? 1 : 0);
    days.push({ readings: readings.slice(cursor, cursor + size) });
    cursor += size;
  }
  return days;
}
